import { Fragment, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { ContractLink } from "@/components/ui/ContractLink";
import { rungOf } from "@/lib/strikeRung";
import { useLiveMtm, useLiveTradeMtm } from "@/lib/useLiveMtm";
import type { ExecutionMode, ExitReason, TradeLegEntry, TradeLogEntry } from "@/types/tradeLog";

interface Props {
  trades: TradeLogEntry[];
}

export function TradesTable({ trades }: Readonly<Props>): ReactElement {
  return (
    <section className="card mb-3 overflow-hidden p-0">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <div className="flex items-center gap-2">
          <i className="ti ti-list-check text-lg text-accent" />
          <h3>Trades</h3>
        </div>
        <span className="text-xs text-ink-muted">{String(trades.length)} trades in view</span>
      </div>
      {trades.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-ink-secondary">
          No trades match the current filters.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <TableHead />
            <tbody>
              {trades.map((t) =>
                t.horizon === "POSITIONAL" ? (
                  <BasketRows key={t.id} trade={t} />
                ) : (
                  <TradeRow key={t.id} trade={t} />
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function TableHead(): ReactElement {
  return (
    <thead className="text-[10px] uppercase tracking-wider text-ink-secondary">
      <tr className="border-b border-line">
        <th className="px-3 py-2 text-left">Time (entry -&gt; exit)</th>
        <th className="px-3 py-2 text-left">Strategy</th>
        <th className="px-3 py-2 text-left">Mode</th>
        <th className="px-3 py-2 text-left">Contract</th>
        <th className="px-3 py-2 text-right">Qty</th>
        <th className="px-3 py-2 text-right">Entry -&gt; Exit</th>
        <th className="px-3 py-2 text-right">SL / target</th>
        <th className="px-3 py-2 text-right">P&amp;L</th>
        <th className="px-3 py-2 text-center">Trail</th>
        <th className="px-3 py-2 text-left">Result</th>
      </tr>
    </thead>
  );
}

/** Which RULE opened the trade, under the strategy that owns it.
 *
 * The strategy name is not the answer to "why did this trade happen" — bias
 * alone runs four independent rules. On 2026-08-24 both of its trades came from
 * GAP_FILL while area A and area B refused all session, and the log could not
 * show that.
 *
 * Renders nothing at all when null: a row from before this was recorded must
 * not be labelled, and a dash under every supertrend row would be noise. */
function SourceLabel({ source }: Readonly<{ source: string | null }>): ReactElement | null {
  if (source === null || source === "") return null;
  return <div className="text-[11px] text-ink-muted">{SOURCE_LABEL[source] ?? source}</div>;
}

/** Bias areas and EMA cases, spelled the way the rules are discussed. Unknown
 * values fall through to the raw string rather than being hidden. */
const SOURCE_LABEL: Record<string, string> = {
  AREA_A: "area A",
  B1: "area B — B1",
  B2: "area B — B2",
  GAP_FILL: "gap fill",
  CONTINUATION: "continuation",
  REVERSAL: "reversal",
  "POI1:LOWER_WICK": "POI-1 — lower wick",
  "POI1:UPPER_WICK": "POI-1 — upper wick",
  "POI1:GREEN_BODY": "POI-1 — green body",
  "POI1:RED_BODY": "POI-1 — red body",
};

/** The rule's own stop and target, as levels on the INDEX.
 *
 * ⚠️ Deliberately NOT merged with `currentSlPrice`: that column is a PREMIUM
 * level fed by a broker stop order, and the rules that watch an index level
 * park no such order — which is exactly why every bias row showed a blank SL
 * while the engine was watching one tick by tick. Two different quantities in
 * one column would read as one.
 *
 * A null target is normal, not missing data: area B rides the VIX ladder and
 * the EMA reversal aims at a moving 50 EMA, so neither commits to a price. */
function SignalLevels({
  stop,
  target,
}: Readonly<{ stop: number | null; target: number | null }>): ReactElement {
  if (stop === null && target === null) return <span className="text-ink-muted">—</span>;
  return (
    <>
      <div className="font-mono tabular-nums">{stop !== null ? stop.toFixed(2) : "—"}</div>
      <div className="font-mono text-[11px] tabular-nums text-ink-muted">
        {target !== null ? target.toFixed(2) : "no target"}
      </div>
    </>
  );
}

function exitTimeLabel(trade: TradeLogEntry): string {
  if (trade.exitAt !== null) return formatTime(trade.exitAt);
  return trade.exitReason === "OPEN" ? "open" : "—";
}

/** Entry -> exit clock, with the date and holding time underneath. Its own
 * component so `TradeRow` stays under the line gate. */
function TimeCell({ trade }: Readonly<{ trade: TradeLogEntry }>): ReactElement {
  return (
    <td className="px-3 py-2.5 align-top">
      <div className="font-medium">
        {formatTime(trade.entryAt)}
        {" -> "}
        {exitTimeLabel(trade)}
      </div>
      <div className="text-[11px] text-ink-muted">
        {formatDate(trade.entryAt)}
        {trade.exitAt !== null ? ` · held ${formatDuration(trade.entryAt, trade.exitAt)}` : ""}
      </div>
    </td>
  );
}

/** "PUT · ITM-3": the side, and how far in the money the strike bought was. */
function biasAndRung(trade: TradeLogEntry): string {
  const rung = rungOf(trade);
  const bias = trade.bias ?? "—";
  if (rung === null) return bias;
  return `${bias} · ${rung.derived ? "~" : ""}${rung.label}`;
}

function TradeRow({ trade }: Readonly<{ trade: TradeLogEntry }>): ReactElement {
  return (
    <tr className="border-b border-line last:border-0 hover:bg-surface-2">
      <TimeCell trade={trade} />
      <td className="px-3 py-2.5 align-top">
        <StrategyLink id={trade.strategyId} name={trade.strategyName} />
        <SourceLabel source={trade.signalSource} />
      </td>
      <td className="px-3 py-2.5 align-top">
        <ModePill mode={trade.executionMode} />
      </td>
      <td className="px-3 py-2.5 align-top">
        <ContractLink symbol={trade.contractSymbol} expiry={trade.contractExpiry} />
        <div className="text-[11px] text-ink-muted">{biasAndRung(trade)}</div>
      </td>
      <td className="px-3 py-2.5 text-right align-top">
        <div>{trade.lots !== null ? `${String(trade.lots)}L` : "—"}</div>
        <div className="text-[11px] text-ink-muted">
          {trade.quantity !== null ? `${String(trade.quantity)} qty` : "—"}
        </div>
      </td>
      <td className="px-3 py-2.5 text-right align-top">
        <div>
          {trade.entryPremium !== null ? `Rs ${trade.entryPremium.toFixed(2)}` : "—"} -&gt;{" "}
          {trade.exitPremium !== null ? `Rs ${trade.exitPremium.toFixed(2)}` : "—"}
        </div>
      </td>
      <td className="px-3 py-2.5 text-right align-top">
        <SignalLevels stop={trade.signalStop} target={trade.signalTarget} />
      </td>
      <td className="px-3 py-2.5 text-right align-top font-medium">
        {trade.exitReason === "OPEN" ? <TradeMtmCell trade={trade} /> : <PnlCell pnl={trade.pnl} />}
      </td>
      <td className="px-3 py-2.5 text-center align-top">
        <span className="pill bg-surface-2 text-ink-secondary">x{String(trade.trailCount)}</span>
      </td>
      <td className="px-3 py-2.5 align-top">
        <ResultPill reason={trade.exitReason} />
      </td>
    </tr>
  );
}

/** A positional basket (docs/positional.md phase E): one summary row —
 * held-days, net entry cost, MTM-or-realized P&L — followed by one thin
 * row per leg. Legs are always visible; a basket has at most 4. */
function BasketRows({ trade }: Readonly<{ trade: TradeLogEntry }>): ReactElement {
  const open = trade.exitReason === "OPEN";
  return (
    <Fragment>
      <tr className="border-b border-line hover:bg-surface-2">
        <td className="px-3 py-2.5 align-top">
          <div className="font-medium">
            {formatTime(trade.entryAt)}
            {" -> "}
            {exitTimeLabel(trade)}
          </div>
          <div className="text-[11px] text-ink-muted">
            {formatDate(trade.entryAt)} · held {String(trade.holdingDays ?? 0)}d
            {trade.basketExpiry !== null ? ` · expiry ${trade.basketExpiry}` : ""}
          </div>
        </td>
        <td className="px-3 py-2.5 align-top">
          <StrategyLink id={trade.strategyId} name={trade.strategyName} />
        </td>
        <td className="px-3 py-2.5 align-top">
          <ModePill mode={trade.executionMode} />
        </td>
        <td className="px-3 py-2.5 align-top">
          <span className="pill bg-accent-bg text-accent">
            <i className="ti ti-calendar-week text-xs" />
            Basket · {String(trade.legs?.length ?? 0)} legs
          </span>
        </td>
        <td className="px-3 py-2.5 text-right align-top text-[11px] text-ink-muted">per leg ↓</td>
        <td className="px-3 py-2.5 text-right align-top">
          <NetCostCell cost={trade.netEntryCost} />
        </td>
        {/* A basket has no index-level stop: its rules are premium and per-leg. */}
        <td className="px-3 py-2.5 text-right align-top text-ink-muted">—</td>
        <td className="px-3 py-2.5 text-right align-top font-medium">
          {open ? <MtmCell trade={trade} /> : <PnlCell pnl={trade.pnl} />}
        </td>
        <td className="px-3 py-2.5 text-center align-top text-ink-muted">—</td>
        <td className="px-3 py-2.5 align-top">
          <ResultPill reason={trade.exitReason} />
        </td>
      </tr>
      {(trade.legs ?? []).map((leg) => (
        <LegRow key={`${trade.id}-${String(leg.legIndex)}`} leg={leg} />
      ))}
    </Fragment>
  );
}

function LegRow({ leg }: Readonly<{ leg: TradeLegEntry }>): ReactElement {
  const buy = leg.side === "BUY";
  return (
    <tr className="border-b border-line bg-surface-2/40 text-xs last:border-0">
      <td className="px-3 py-1.5" colSpan={3}>
        <span className={buy ? "text-success" : "text-danger"}>
          {buy ? "+" : "−"} {leg.side}
        </span>
      </td>
      <td className="px-3 py-1.5">
        <ContractLink symbol={leg.contractSymbol} expiry={leg.expiry} />
      </td>
      <td className="px-3 py-1.5 text-right">{String(leg.quantity)} qty</td>
      <td className="px-3 py-1.5 text-right">
        {leg.entryFillPrice !== null ? `Rs ${leg.entryFillPrice.toFixed(2)}` : "—"} -&gt;{" "}
        {leg.exitFillPrice !== null ? `Rs ${leg.exitFillPrice.toFixed(2)}` : "—"}
      </td>
      <td className="px-3 py-1.5" />
      <td className="px-3 py-1.5 text-right text-ink-muted" colSpan={2}>
        {leg.expiry}
      </td>
      <td className="px-3 py-1.5 text-ink-secondary">{leg.status}</td>
    </tr>
  );
}

/** The row's strategy, as a same-tab link to its detail page (operator,
 * 2026-08-10: reaching a strategy from the log took Strategies → find →
 * click every time). Same destination the Open Position card links to. */
function StrategyLink({ id, name }: Readonly<{ id: string; name: string }>): ReactElement {
  return (
    <Link
      to={`/strategies/${id}`}
      className="inline-flex items-center gap-0.5 text-accent hover:underline"
    >
      {name}
      <i className="ti ti-chevron-right text-xs" />
    </Link>
  );
}

/** Net entry cost C: paid (debit) or received (credit) up front. */
function NetCostCell({ cost }: Readonly<{ cost: number | null }>): ReactElement {
  if (cost === null) return <span className="text-ink-muted">—</span>;
  const label = cost >= 0 ? "debit" : "credit";
  return (
    <span>
      Rs {Math.abs(Math.round(cost)).toLocaleString("en-IN")}{" "}
      <span className="text-[11px] text-ink-muted">{label}</span>
    </span>
  );
}

/** Unrealized mark on an open basket — LIVE, the same 5s leg-LTP poll the
 * Strategy page's BasketTile uses (operator, 2026-08-07: the two pages
 * disagreed). Falls back to the stored `lastMtm` when the live mark can't
 * be computed — flat basket, no broker session, quotes not landed — and
 * says so, because that snapshot is written once a day at 15:25 IST and
 * is otherwise indistinguishable from a live number. */
function MtmCell({ trade }: Readonly<{ trade: TradeLogEntry }>): ReactElement {
  const live = useLiveMtm(trade);
  const mtm = live ?? trade.lastMtm;
  if (mtm === null) return <span className="text-ink-muted">MTM pending</span>;
  return <MtmValue mtm={mtm} stale={live === null} />;
}

/** Unrealized mark on an OPEN intraday trade, live off the contract's LTP —
 * the same number and label a positional basket shows. No snapshot exists for
 * intraday, so until a quote lands it stays a dash. */
function TradeMtmCell({ trade }: Readonly<{ trade: TradeLogEntry }>): ReactElement {
  const live = useLiveTradeMtm(trade);
  if (live === null) return <span className="text-ink-muted">—</span>;
  return <MtmValue mtm={live} stale={false} />;
}

function MtmValue({ mtm, stale }: Readonly<{ mtm: number; stale: boolean }>): ReactElement {
  const rounded = Math.round(mtm);
  let cls = "text-ink-secondary";
  let sign = "";
  if (rounded > 0) {
    cls = "text-success";
    sign = "+";
  } else if (rounded < 0) {
    cls = "text-danger";
    sign = "-";
  }
  return (
    <span
      className={cls}
      title={stale ? "Last 15:25 IST mark — no live quotes right now" : undefined}
    >
      {sign}Rs {Math.abs(rounded).toLocaleString("en-IN")}{" "}
      <span className="text-[11px] text-ink-muted">{stale ? "MTM @15:25" : "MTM"}</span>
    </span>
  );
}

/** Gross P&L, straight from the broker fills — matches what the operator
 * sees on Kite. Sign always explicit. Charges live on the summary card's
 * editable Brokerage stat, not per row. */
function PnlCell({ pnl }: Readonly<{ pnl: number | null }>): ReactElement {
  if (pnl === null) return <span className="text-ink-muted">—</span>;
  const rounded = Math.round(pnl);
  if (rounded > 0)
    return <span className="text-success">+Rs {rounded.toLocaleString("en-IN")}</span>;
  if (rounded < 0)
    return <span className="text-danger">-Rs {Math.abs(rounded).toLocaleString("en-IN")}</span>;
  return <span className="text-ink-secondary">Rs 0</span>;
}

/** Live vs Paper per row — instant reassurance about whether real money
 * moved, independent of which filter the view is on. */
function ModePill({ mode }: Readonly<{ mode: ExecutionMode }>): ReactElement {
  if (mode === "PAPER") {
    return (
      <span className="pill bg-warning-bg text-warning" data-testid="mode-pill">
        <i className="ti ti-flask text-xs" />
        Paper
      </span>
    );
  }
  return (
    <span className="pill bg-success-bg text-success" data-testid="mode-pill">
      <i className="ti ti-rocket text-xs" />
      Live
    </span>
  );
}

function ResultPill({ reason }: Readonly<{ reason: ExitReason }>): ReactElement {
  const map: Record<ExitReason, { cls: string; label: string; icon: string }> = {
    TARGET_HIT: { cls: "bg-success-bg text-success", label: "Target hit", icon: "ti-target-arrow" },
    SL_HIT: { cls: "bg-danger-bg text-danger", label: "SL hit", icon: "ti-shield-off" },
    LOSS_CAP: { cls: "bg-danger-bg text-danger", label: "Loss cap", icon: "ti-currency-rupee" },
    TRAIL_EXIT: {
      cls: "bg-success-bg text-success",
      label: "Trail exit",
      icon: "ti-trending-down",
    },
    FORCE_SQUAREOFF: {
      cls: "bg-warning-bg text-warning",
      label: "Force-squared",
      icon: "ti-clock-stop",
    },
    MANUAL: { cls: "bg-surface-2 text-ink-secondary", label: "Manual", icon: "ti-hand-stop" },
    EXIT_FAILED: {
      cls: "bg-danger-bg text-danger",
      label: "Exit failed",
      icon: "ti-alert-triangle",
    },
    OPEN: { cls: "bg-accent-bg text-accent", label: "Open", icon: "ti-circle-dot" },
  };
  const m = map[reason];
  return (
    <span className={`pill ${m.cls}`}>
      <i className={`ti ${m.icon} text-xs`} />
      {m.label}
    </span>
  );
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDuration(startIso: string, endIso: string): string {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${String(minutes)}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h)}h ${String(m).padStart(2, "0")}m`;
}
